import { TotemInput } from 'components/TotemInput';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemSelect } from 'components/TotemSelect';
import {
    GetDetailedProductQuery,
    useGetProductArrangementsQuery,
    useUpdateProductArrangementMutation,
} from 'data/__generated__';
import { GET_DETAILED_PRODUCT_QUERY } from 'data/queries/product';
import { FunctionComponent, useState } from 'react';
import styled from 'styled-components';

type AddProductArrangementPopupProps = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    product: NonNullable<GetDetailedProductQuery['detailedProduct']>;
};

export const AddProductArrangementPopup: FunctionComponent<AddProductArrangementPopupProps> = ({
    isOpen,
    setIsOpen,
    product,
}) => {
    // La popup est montée fermée avec sa section : la liste des gammes ne part qu'à l'ouverture.
    const { data } = useGetProductArrangementsQuery({ skip: !isOpen });

    const [selectedProductArrangementId, setSelectedProductArrangementId] = useState<string | null>(null);
    const [stockMax, setStockMax] = useState<number | null>(null);

    const [updateProductArrangement, { loading }] = useUpdateProductArrangementMutation({
        onCompleted: (data) => {
            if (!data.updateProductArrangementMutation) {
                return;
            }
            setIsOpen(false);
        },
        // refetching queries instead of cache update because shape of productArrangements in query and mutation are different
        // which would lead to a hard to maintain cache update.
        refetchQueries: [{ query: GET_DETAILED_PRODUCT_QUERY, variables: { productId: product._id } }],
        awaitRefetchQueries: true,
    });

    if (!data?.productArrangementsQuery) {
        return null;
    }

    const allOptions = data.productArrangementsQuery.map((pa) => ({ label: pa.name, value: pa._id }));
    const options = allOptions.filter((option) => !product.productArrangements.some((pa) => pa._id === option.value));

    const selectedOption = options.find((option) => option.value === selectedProductArrangementId);
    const selectedProductArrangement = data.productArrangementsQuery.find(
        (pa) => pa._id === selectedProductArrangementId,
    );

    const onSubmit = () => {
        if (!selectedProductArrangementId || !selectedProductArrangement) {
            return;
        }

        const newProductIdsWithStockMax = [
            ...selectedProductArrangement.productIdsWithStockMax.map(({ productId, stockMax }) => ({
                productId,
                stockMax,
            })),
            {
                productId: product._id,
                stockMax: stockMax ?? 0,
            },
        ];

        updateProductArrangement({
            variables: {
                productArrangement: {
                    _id: selectedProductArrangementId,
                    name: selectedProductArrangement.name,
                    reassortmentType: selectedProductArrangement.reassortmentType,
                    productIdsWithStockMax: newProductIdsWithStockMax,
                },
            },
        });
    };

    return (
        <TotemPopup
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            title="Ajouter à une gamme de produit"
            contentOverflow="visible"
        >
            <Container>
                <TotemSelect
                    label="Gamme de produit"
                    options={options}
                    value={selectedProductArrangementId}
                    onChange={(value) => {
                        setSelectedProductArrangementId(value);
                        const pa = data.productArrangementsQuery.find((el) => el._id === value);
                        setStockMax(pa?.defaultStockMax ?? null);
                    }}
                />
                {selectedOption && (
                    <TotemInput
                        label="Stock max"
                        type="number"
                        onChange={(value) => {
                            const parsedValue = parseInt(value, 10);
                            setStockMax(parsedValue);
                        }}
                        value={stockMax ?? undefined}
                    />
                )}
                <TotemPrimaryButton
                    onClick={onSubmit}
                    disabled={!selectedProductArrangementId || stockMax === null || loading}
                >
                    Ajouter
                </TotemPrimaryButton>
            </Container>
        </TotemPopup>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;
